import { useEffect, useState } from "react";
import { Control, Controller, useForm } from "react-hook-form";
import { useLazyQuery, useMutation } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { Checkbox } from "@cofano/cck";
import { Box, CircularProgress, Grid, Tooltip, Typography } from "@mui/material";

import { FormDialog } from "components/dialogs/Dialogs";

import { DataFormat } from "utils";

import { TankConfiguration, TankPosition } from "__generated__/enums";
import { GetAssignableShipTanksDocument, GetAssignableShipTanksQuery } from "__generated__/view/voyages/graphql/voyage.queries.generated";
import { UpdateCargoTanksDocument } from "__generated__/view/voyages/graphql/voyage.mutations.generated";
import { ChildProps, ChildPropsFromArray } from "types/commons";

type Tank = ChildPropsFromArray<ChildProps<GetAssignableShipTanksQuery, "ship">, "tanks">;

type CargoTankAssignmentDialogProps = {
	voyageId: number;
	shipId: number;
	open: boolean;
	onClose: () => void;
	refetch: () => void;
	cargo: {
		id: number;
		tanks?: Tank[] | null;
	};
}

type CargoTankAssignmentForm = {
	tanks: Tank[];
}

type TankCheckboxProps = {
	tank: Tank;
	control: Control<CargoTankAssignmentForm>;
	selectedTanks: Tank[];
	onSelectTank: (tank: Tank) => void;
	tankConfiguration: TankConfiguration | null;
}

const TankCheckbox = ({ tank, control, selectedTanks, onSelectTank, tankConfiguration }: TankCheckboxProps) => {
	return (
		<span style={{ margin: 8 }}>
			<Controller
				name="tanks"
				control={control}
				render={() => (
					<Tooltip title={!tank.isAssignable ? intl.formatMessage({ id: "TANK_ALREADY_ASSIGNED" }) : ""}>
						<Checkbox
							disabled={!tank.isAssignable}
							checked={selectedTanks.map(t => t.id).includes(tank.id)}
							onChange={() => onSelectTank(tank)}
							label={`
									${tankConfiguration === TankConfiguration.PortStarboardTanks ? (intl.formatMessage({ id: `TANK_${tank.position}_PREFIX` })) : ""}
									${intl.formatMessage({ id: "TANK" })}
									${tank.positionNumber}  ${tank.capacityM3 ? `(${DataFormat.volumeM3(tank.capacityM3)})` : ""}
								`}
						/>
					</Tooltip>
				)}
			/>
		</span>
	);
};

const CargoTankAssignmentDialog = ({ voyageId, shipId, open, refetch, onClose, cargo }: CargoTankAssignmentDialogProps) => {
	const [ tanks, setTanks ] = useState<Tank[]>([]);
	const [ tankConfiguration, setTankConfiguration ] = useState<TankConfiguration | null>(null);

	const [ saveCargoTanks, { loading } ] = useMutation(UpdateCargoTanksDocument, {
		onCompleted: () => {
			onClose();
			refetch();
		},
	});

	const [ getShip, { loading: loadingTanks } ] = useLazyQuery(GetAssignableShipTanksDocument, {
		onCompleted: data => {
			setTankConfiguration(data.ship.tankProperties?.tankConfiguration ?? null);
			setTanks(data.ship.tanks);
		},
	});

	const defaultValues: CargoTankAssignmentForm = {
		tanks: cargo?.tanks || [],
	};

	const { control, handleSubmit, watch, setValue, reset } = useForm({
		defaultValues: defaultValues,
	});

	const selectedTanks: Tank[] = watch("tanks");

	const onSelectTank = (tank: Tank) => {
		const isSelected = selectedTanks.map(t => t.id).includes(tank.id);
		const updatedTanks = isSelected
			? selectedTanks.filter((t) => t.id !== tank.id)
			: [ ...selectedTanks, tank ];

		setValue("tanks", updatedTanks);
	};

	const onSubmit = (values: CargoTankAssignmentForm) => {
		if (cargo == null) {
			return;
		}
		saveCargoTanks({
			variables: {
				input: {
					cargoId: cargo.id,
					tankIds: values.tanks.map(t => t.id),
				},
			},
		});
	};

	useEffect(() => {
		if (cargo == null) {
			return;
		}
		getShip({
			variables: {
				voyageId: voyageId,
				cargoId: cargo.id,
				shipId: shipId,
			},
		});
	}, [ voyageId, shipId, cargo ]);

	useEffect(() => {
		reset(defaultValues);
	}, [ cargo ]);

	if (cargo == null) {
		return <></>;
	}

	return (
		<FormDialog
			open={open}
			title={intl.formatMessage({ id: "TANK_ASSIGNMENT" })}
			onClose={onClose}
			form="cargoTanksForm"
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(onSubmit)(event)} id="cargoTanksForm">
				<Grid container spacing={3}>
					{loadingTanks ? (
						<Grid item xs={12}>
							<Box sx={{ display: "flex", justifyContent: "center" }}>
								<CircularProgress />
							</Box>
						</Grid>
					) : tanks.length > 0 ? (
						tankConfiguration === TankConfiguration.PortStarboardTanks ? (
							<>
								<Grid item xs={6}>
									{tanks.filter(t => t.position === TankPosition.Port).map((tank) => (
										<TankCheckbox
											key={tank.id}
											tank={tank}
											control={control}
											selectedTanks={selectedTanks}
											onSelectTank={onSelectTank}
											tankConfiguration={tankConfiguration}
										/>
									))}
								</Grid>
								<Grid item xs={6}>
									{tanks.filter(t => t.position === TankPosition.Starboard).map((tank) => (
										<TankCheckbox
											key={tank.id}
											tank={tank}
											control={control}
											selectedTanks={selectedTanks}
											onSelectTank={onSelectTank}
											tankConfiguration={tankConfiguration}
										/>
									))}
								</Grid>
							</>
						) : (
							<Grid item xs={6}>
								{tanks.map((tank) => (
									<TankCheckbox
										key={tank.id}
										tank={tank}
										control={control}
										selectedTanks={selectedTanks}
										onSelectTank={onSelectTank}
										tankConfiguration={tankConfiguration}
									/>
								))}
							</Grid>
						)
					) : (
						<Grid item xs={12}>
							<Box sx={{ display: "flex", justifyContent: "center" }}>
								<Typography
									component="span"
									color="grey"
									fontSize={14}
									fontStyle="italic">
									{intl.formatMessage({ id: "SHIP_TANKS_NOT_LINKED" })}
								</Typography>
							</Box>
						</Grid>
					)}
				</Grid>
			</form>
		</FormDialog>
	);
};

export default CargoTankAssignmentDialog;
